import type { InvitableRole, OrganizationRole } from '@/api/organizations';

export const ORGANIZATION_NAME_MAX_LENGTH = 100;

export const ROLE_INFO: Record<
  OrganizationRole,
  { label: string; description: string; badge: string }
> = {
  OWNER: {
    label: 'Owner',
    description: 'Everything, including billing, members and invitations.',
    badge:
      'bg-indigo-50 text-indigo-700 inset-ring-indigo-700/10 dark:bg-indigo-400/10 dark:text-indigo-300 dark:inset-ring-indigo-400/30',
  },
  EDITOR: {
    label: 'Editor',
    description: 'Create, change, pause and delete runners and schedules.',
    badge:
      'bg-green-50 text-green-700 inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20',
  },
  VIEWER: {
    label: 'Viewer',
    description: 'See runners, their status and metrics. No changes.',
    badge:
      'bg-gray-50 text-gray-600 inset-ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:inset-ring-gray-400/20',
  },
};

export const INVITABLE_ROLES: InvitableRole[] = ['EDITOR', 'VIEWER'];

export function roleLabel(role: OrganizationRole | null | undefined): string {
  return role ? ROLE_INFO[role].label : '';
}

/** A wait such as "45 s", "9 min 5 s" or "2 h 30 min". */
export function formatWait(seconds: number): string {
  const total = Math.max(0, Math.ceil(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const rest = total % 60;
  if (hours > 0) return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  if (minutes > 0) return rest > 0 ? `${minutes} min ${rest} s` : `${minutes} min`;
  return `${rest} s`;
}

export function validateOrganizationName(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Enter a name.';
  if (trimmed.length > ORGANIZATION_NAME_MAX_LENGTH) {
    return `Use at most ${ORGANIZATION_NAME_MAX_LENGTH} characters.`;
  }
  return null;
}

interface ProblemBody {
  code?: string;
  field?: string;
  detail?: string;
}

export interface OrganizationApiError {
  message: string;
  status?: number;
  code?: string;
  field?: string;
}

const CODE_MESSAGES: Record<string, string> = {
  ORGANIZATION_NOT_FOUND: 'This organization no longer exists or you are no longer a member.',
  ORGANIZATION_PERMISSION_DENIED: 'Your role in this organization does not allow this.',
  ORGANIZATION_MEMBER_NOT_FOUND: 'This person is no longer a member.',
  INVITATION_NOT_FOUND: 'This invitation no longer exists.',
  INVITATION_INVALID:
    'This invitation is invalid, has expired or was already used. Ask the organization owner to send a new one.',
  INVITATION_EMAIL_MISMATCH: 'This invitation was sent to a different email address.',
  ALREADY_MEMBER: 'This person is already a member of the organization.',
  OWNER_ROLE_IMMUTABLE: "The owner's role cannot be changed.",
  OWNER_CANNOT_LEAVE: 'The owner cannot leave the organization.',
  OWNER_CANNOT_BE_REMOVED: 'The owner cannot be removed.',
  EMAIL_SEND_FAILED:
    'The invitation email could not be sent. The invitation is still pending: resend it in 10 minutes or later, as the sending limits allow.',
};

const FIELD_MESSAGES: Record<string, string> = {
  name: `Enter a name of at most ${ORGANIZATION_NAME_MAX_LENGTH} characters.`,
  email: 'Enter a valid email address.',
  role: 'Choose Editor or Viewer.',
  token: 'This invitation link is incomplete.',
};

export function organizationApiError(e: unknown, action: string): OrganizationApiError {
  const { response } = (e ?? {}) as { response?: { status?: number; data?: unknown } };
  if (!response) {
    return {
      message: `Could not ${action}: the server is unreachable. Check your connection and try again.`,
    };
  }
  const status = response.status;
  const body = (
    response.data && typeof response.data === 'object' ? response.data : {}
  ) as ProblemBody;
  const code = body.code;

  if (status === 400 && code === 'INVALID_ORGANIZATION_REQUEST') {
    const field = body.field;
    return {
      message: (field && FIELD_MESSAGES[field]) || 'The request was rejected.',
      status,
      code,
      field,
    };
  }
  if (code && CODE_MESSAGES[code]) return { message: CODE_MESSAGES[code], status, code };
  if (status === 403) {
    // Spring Security rejects sessions without completed MFA before any organization check.
    return {
      message:
        'You are not allowed to do this with this session. Sign in again, completing multi-factor authentication if enabled.',
      status,
    };
  }
  return { message: `Could not ${action} (HTTP ${status ?? 'error'}). Try again later.`, status };
}
